import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { ThemeProvider } from "@emotion/react";
import { createTheme } from "@mui/material";
import Fields from "./fields";
import { defaultFormTheme } from "../../../../../../atoms/defaultFormTheme";
import {
  ScheduleCallFormDefault,
  ScheduleCallFormTypes,
  ZoomSetttingsFormDefault,
  getFormDefaultValues,
} from "../helper";
import { RequestServer } from "../../../../../../utils/services";
import { APIRoutes } from "../../../../../../constants";
import { usePeMappingContext } from "../../../helper";
import { useSnackbar } from "notistack";
import { LocalDayjs, getISTDateTime } from "../../../../../../utils/timezoneService";
import { Dayjs } from "dayjs";
import { useFullPageLoading } from "../../../../../../atoms/full-page-loading/loadingContext";
import { removeWhiteSpacesFromForm } from "../../../../../../utils/utils";
import { formatCallDuration, functionReturnZoomObject } from "./helper";
import { useState, useEffect, useMemo } from "react";
import SERestrictionDialog from "../scPopupDialog";
type Props = {
  allowUseContext: boolean;
  handleClose: () => void;
  handleSubmitClose: () => void;
  pe_id: number;
  project_id: string | null;
  startTime?: Dayjs | null;
  duration?: number | null;
  openCallError: (text: string) => void;
  openCallDetails: (id: number) => void;
  client_participant_invitation_text: string;
  formdefaultValues?: ScheduleCallFormTypes;
  call_id?: number;
  pe_meta?: any;
  client_id?: number | null;
};


const ShortlistForm = ({ allowUseContext, handleClose, handleSubmitClose, pe_id, project_id, startTime,
  duration, openCallError, formdefaultValues, call_id, openCallDetails, client_participant_invitation_text, pe_meta, client_id }: Props) => {

  const [openDialog, setDialogOpen] = useState<boolean>(false);
  const [errorDetails, setErrorDetails] = useState<any[]>([]);
  const [checked, setChecked] = useState<boolean>(false);
  const [restrictedFormData, setRestrictedFormData] = useState<any>(null);
  const [defaultValues, setDefaultValues] = useState<ScheduleCallFormTypes | null>(null);
    const [isCallMediumDisabled, setIsCallMediumDisabled] = useState<boolean>(false);
  const { refetch } = allowUseContext ? usePeMappingContext() : { refetch: () => { } };
  const { enqueueSnackbar } = useSnackbar();
  const { setLoading } = useFullPageLoading();

  // Initialize form methods with a temporary default, will be reset when defaultValues loads
  const methods = useForm({
    defaultValues: formdefaultValues 
  });
  // Memoize formdefaultValues to prevent unnecessary re-renders
  const memoizedFormDefaultValues = useMemo(() => formdefaultValues, [formdefaultValues]);

  // Effect to load default values
  useEffect(() => {
    const loadDefaultValues = async () => {
      try {
        setLoading(true);
        
        let computedDefaultValues;
        
        if (memoizedFormDefaultValues) {
          // Use provided form default values
          computedDefaultValues = { ...memoizedFormDefaultValues };
        } else if (project_id) {
          // Fetch dynamic default values
          computedDefaultValues = await getFormDefaultValues(pe_id, project_id);
        } else {
          // Fallback to static default
          computedDefaultValues = { ...ScheduleCallFormDefault };
        }

        // Apply duration if provided
        if (duration) {
          const dur = formatCallDuration(duration);
          computedDefaultValues.duration = dur;
          computedDefaultValues.scheduled_start_time = startTime;
        }

          if(!!call_id && client_id === 53){
            setIsCallMediumDisabled(true);
          }

          if (pe_meta.do_not_share_outside) {
            computedDefaultValues.call_medium = { label: "Others", value: "Others" };
            if(client_id === 53){
              setIsCallMediumDisabled(true);
            }
          } 

        // Add invitation text if not already provided
        if (client_participant_invitation_text && !computedDefaultValues.invitation_text) {
          computedDefaultValues.invitation_text = client_participant_invitation_text;
        }

        setDefaultValues(computedDefaultValues);
        
        // Reset form with new default values
        methods.reset(computedDefaultValues);
        
      } catch (error) {
        console.error('Error loading default values:', error);
        // Fallback to static default on error
        const fallbackValues = { ...ScheduleCallFormDefault };
        
        if (duration) {
          const dur = formatCallDuration(duration);
          fallbackValues.duration = dur;
          fallbackValues.scheduled_start_time = startTime;
        }

        if (client_participant_invitation_text && !fallbackValues.invitation_text) {
          fallbackValues.invitation_text = client_participant_invitation_text;
        }
        setDefaultValues(fallbackValues);
        methods.reset(fallbackValues);
      } finally {
        setLoading(false);
      }
    };

    loadDefaultValues();
  }, [pe_id, project_id]);

  const defaultTheme = createTheme(defaultFormTheme);
  const onSubmit: SubmitHandler<ScheduleCallFormTypes> = async (formData) => {
    const isEdit = !!call_id;
    const newFormData = removeWhiteSpacesFromForm(formData, [])

    if (!newFormData.scheduled_start_time) {
      enqueueSnackbar("Start time required", {
        variant: "warning",
      });
      return
    }

    if (!newFormData.call_title_for_expert) {
      enqueueSnackbar("Expert call title required", {
        variant: "warning",
      });
      return
    }

    if (!newFormData.invitation_text_for_expert) {
      enqueueSnackbar("Expert invitation text required", {
        variant: "warning",
      });
      return
    }

    if (!newFormData.client_participants.length) {
      enqueueSnackbar("Client participants required", {
        variant: "warning",
      });
      return
    }

    if (!newFormData.invitation_text) {
      enqueueSnackbar("Client invitation text required", {
        variant: "warning"
      })
      return;
    }

    if (!newFormData.title) {
      enqueueSnackbar("Client title required", {
        variant: "warning"
      })
      return;
    }

    if (!newFormData.call_medium) {
      enqueueSnackbar("Call medium required", {
        variant: "warning"
      });
      return;
    }

    const payload: any = {
      check_restrictions: checked ? false : true,
      action: isEdit ? "RescheduleCall" : "ScheduleCall",
      pe_id: pe_id,
      scheduled_start_time: getISTDateTime(newFormData.scheduled_start_time),
      scheduled_end_time: newFormData.duration ? getISTDateTime(LocalDayjs(newFormData.scheduled_start_time).add(+newFormData.duration.value, "minutes")) : null,
      title: newFormData.title,
      invitation_text: newFormData.invitation_text,
      client_participants: newFormData.client_participants.map(item => item.value).join(","),
      call_title_for_expert: newFormData.call_title_for_expert,
      invitation_text_for_expert: newFormData.invitation_text_for_expert,
      do_not_send_email: newFormData.doNotSendEmail ? 1 : 0,
      research_analyst: newFormData.research_analyst?.value,
      name_masking: newFormData?.zoomSettings?.masking_participant || false,
      call_medium: newFormData.call_medium?.value,
    };

    if (call_id) {
      payload.id = call_id;
    }


    if (newFormData.videoLinkOption == 'zoom') {
      payload['zoomSettings'] = functionReturnZoomObject(newFormData, newFormData.zoomSettings || ZoomSetttingsFormDefault);
    } else if (newFormData.videoLinkOption == 'manual') {
      payload['call_link'] = newFormData.meetingLink;
    }
    if (newFormData.selected_agendas.length) {
      payload.selected_agendas = newFormData.selected_agendas.map((agenda: any) => agenda.value)
    }
    setLoading(true);
    try {
      const response = await RequestServer(
        APIRoutes.scheduleCall,
        isEdit ? "PATCH" : "POST",
        payload
      );

      if (response.success) {
        enqueueSnackbar(`Call scheduled Successfully.`, {
          variant: "success",
        });

        const id = response?.data?.id || call_id;
        openCallDetails && openCallDetails(id);
        if (allowUseContext) {
          refetch();
        }
      } else {
        if (response?.restricted) {
          setRestrictedFormData(formData);
          setErrorDetails(response?.restrictions);
          setDialogOpen(true);
          return;
        }
        enqueueSnackbar(response.message.toString(), {
          variant: "warning",
        });
        openCallError(response.message.toString());
      }
    } catch (err) {
      console.log(err);
      enqueueSnackbar("Request failed.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleFinalSubmit = () => {
    if (checked) {
      onSubmit(restrictedFormData);
      setRestrictedFormData(null);
      setDialogOpen(false);
    }
  };
  // Don't render the form until default values are loaded
  if (!defaultValues) {
    return null; // or a loading spinner
  }
  return (
    <FormProvider {...methods}>
      <ThemeProvider theme={defaultTheme}>
        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
          <Fields handleClose={handleClose} pe_id={pe_id} pe_meta={pe_meta} project_id={project_id} isCallMediumDisabled={isCallMediumDisabled} />
        </form>
        <SERestrictionDialog
          openDialog={openDialog}
          setDialogOpen={setDialogOpen}
          errorDetails={errorDetails}
          checked={checked}
          setChecked={setChecked}
          handleFinalSubmit={handleFinalSubmit}
        />
      </ThemeProvider>
    </FormProvider>
  );
};

export default ShortlistForm;
